<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import { IClubActivityItem } from '@/interfaces/IClubActivity';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = withDefaults(
    defineProps<{
      item: IClubActivityItem;
      /** Program-wide feeds also show who the movement belongs to. */
      showMember?: boolean;
    }>(),
    { showMember: false }
  );

  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');

  const label = computed(() => {
    const key = `club.activity.${props.item.source}.${props.item.type}`;
    const translated = i18nT(key);
    // vue-i18n returns the key itself for unknown entries.
    return translated === key ? props.item.type : translated;
  });

  const amountText = computed(() => {
    if (props.item.source === 'points') {
      const points = props.item.points ?? 0;
      return `${points > 0 ? '+' : ''}${points.toLocaleString('es-AR')} pts`;
    }
    const amount = props.item.amount ?? 0;
    const formatted = `${symbol.value}${Math.abs(amount).toLocaleString(
      'es-AR'
    )}`;
    return amount < 0 ? `−${formatted}` : `+${formatted}`;
  });

  const positive = computed(
    () =>
      ((props.item.source === 'points'
        ? props.item.points
        : props.item.amount) ?? 0) > 0
  );

  /** Entries written by a reversal-style operation get a subtle marker. */
  const isReversalType = computed(() =>
    ['revoke', 'expire', 'reversal', 'refund_credit'].includes(props.item.type)
  );

  const isManualAdjust = computed(() => props.item.type === 'adjust');

  function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
</script>

<template>
  <div class="v2-club-activity-row">
    <div class="activity-main">
      <span v-if="showMember && item.memberName" class="activity-member">
        {{ item.memberName }}
        <span v-if="item.memberNumber" class="activity-member-number">
          {{ $t('club.member.number', { number: item.memberNumber }) }}
        </span>
      </span>
      <span class="activity-label">
        {{ label }}
        <PosUiBadge
          v-if="isReversalType"
          color="warning"
          variant="soft"
          size="xs"
        >
          {{ $t('club.activity.reversalTag') }}
        </PosUiBadge>
        <PosUiBadge
          v-else-if="isManualAdjust"
          color="info"
          variant="soft"
          size="xs"
        >
          {{ $t('club.activity.manualTag') }}
        </PosUiBadge>
      </span>
      <span class="activity-meta">
        {{ formatDateTime(item.createdAt) }}
        <template v-if="item.orderId">
          · {{ $t('club.activity.order', { id: item.orderId }) }}
        </template>
        <template v-if="item.actorName">
          · {{ $t('club.activity.by', { name: item.actorName }) }}
        </template>
      </span>
      <span v-if="item.note" class="activity-note">
        {{ item.note }}
      </span>
    </div>
    <span class="activity-amount" :class="positive ? 'positive' : 'negative'">
      {{ amountText }}
    </span>
  </div>
</template>

<style scoped lang="scss">
  .v2-club-activity-row {
    @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-100 tw-py-2.5 last:tw-border-b-0;

    .activity-main {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5;

      .activity-member {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-neutral-900;

        .activity-member-number {
          @apply tw-text-xs tw-font-normal tw-tabular-nums tw-text-neutral-400;
        }
      }

      .activity-label {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-neutral-800;
      }

      .activity-meta {
        @apply tw-text-xs tw-text-neutral-400;
      }

      .activity-note {
        @apply tw-text-xs tw-italic tw-text-neutral-500;
      }
    }

    .activity-amount {
      @apply tw-flex-none tw-text-sm tw-font-semibold tw-tabular-nums;

      &.positive {
        @apply tw-text-emerald-600;
      }

      &.negative {
        @apply tw-text-red-600;
      }
    }
  }
</style>
